<template>
  <div class="w-full flex flex-col items-center">
    <div v-if="!adventureRejected" class="w-full px-16 md:px-0 md:max-w-580 mx-auto">
      <slot></slot>
      <Details
        v-if="showDetails"
        :adventureCategoryImageUrl="adventureCategoryImageUrl"
        :title="adventure.title"
        :sitterFirstName="sitterProfile.firstName"
        :sitterLastInitial="sitterProfile.lastInitial"
        :city="sitterProfile.location && sitterProfile.location.cityname"
        :state="sitterProfile.location && sitterProfile.location.stateabbr"
        :sitterTravelDistance="sitterProfile.distanceWillingToTravel"
        :minPayRate="sitterProfile.minPayRate"
        :maxPayRate="sitterProfile.maxPayRate"
        :duration="adventure.duration"
        :surcharge="adventure.surcharge"
        :ageRangeText="adventure.ageRangeText"
        :adventureCategoryName="adventure.adventureCategoryName"
        :description="adventure.description"
        :sitterPassion="adventure.sitterPassion"
        :adventureSitterSupplies="suppliesArray(adventure.adventureSitterSupplies)"
        :adventureParentSupplies="suppliesArray(adventure.adventureParentSupplies)"
        :sitterAvailability="adventure.sitterAvailability"
      />
      <div
        class="w-full fixed bottom-0 left-0 border-t-1 border-solid border-grey-divider-subdued bg-foreground p-16 md:px-0 md:flex md:justify-center"
      >
        <div class="md:w-full md:max-w-580 md:flex md:justify-end">
          <Button
            value="Submit for review"
            size="medium"
            class="w-full md:w-auto"
            :loading="loading"
            @click.prevent="submitAdventure"
          />
        </div>
      </div>
    </div>
    <ReviewRejected v-else-if="adventureRejected" />
    <ReviewError v-if="adventureError" :adventureStatus="adventureStatus" @click="retrySubmit" @close="resetAdventureStatus" />
    <ReviewSuccess v-if="showSuccessPopup" :showPopup="showSuccessPopup" @close="closeSuccessPopup" />
  </div>
</template>

<script lang="ts">
// components
import Button from '../../../../components/button/Button.vue';
import Details from '../../details/Details.vue';
import ReviewRejected from './ReviewRejected.vue';
import ReviewError from './ReviewError.vue';
import ReviewSuccess from './ReviewSuccess.vue';
import { UPDATE_ADVENTURE } from '../../../../graphql/adventures/mutations';

export default {
  name: 'Review',
  components: { Details, Button, ReviewRejected, ReviewError, ReviewSuccess },
  props: {
    sitterProfile: {
      default: () => {},
      type: Object,
    },
    adventureMetaData: {
      default: () => {},
      type: Object,
    },
  },
  data(): any {
    return {
      adventure: this.$store.getters['adventures/adventureDraft'],
      adventureStatus: '',
      loading: false,
      showSuccessPopup: false,
    };
  },
  computed: {
    adventureCategoryImageUrl() {
      return this.$store.getters['adventures/adventureDraft'].adventureCategoryImage?.url;
    },
    showDetails(): boolean {
      return !!this.sitterProfile && !!this.adventure;
    },
    adventureRejected(): boolean {
      return this.adventureStatus === 'rejected';
    },
    adventureError(): boolean {
      return this.adventureStatus === 'error';
    },
  },
  watch: {
    adventureMetaData(metaData) {
      // if the user is attempting to submit with an image that doesn't match the category, change it to the [0] index of the correct category.
      // this is because the graphql mutation doesn't allow for a null to be sent after a user changes their category.
      const hasImageFromCategory = metaData.categories[0].adventureCategoryImages.some(
        (image) => image.id === this.$store.getters['adventures/adventureDraft'].adventureCategoryImage.id
      );
      if (!hasImageFromCategory) {
        this.$store.dispatch('adventures/updateStore', {
          adventureCategoryImage: metaData.categories[0].adventureCategoryImages[0],
        });
      }
    },
  },
  mounted() {
    this.$emit('alert', 4, 'All Adventures are reviewed for appropriate content.');
  },
  methods: {
    suppliesArray(supplies: Array<any>) {
      return supplies.map((obj) => obj.item);
    },
    resetAdventureStatus() {
      this.adventureStatus = '';
    },
    retrySubmit() {
      this.resetAdventureStatus();
      this.submitAdventure();
    },
    closeSuccessPopup() {
      this.showSuccessPopup = false;
    },
    submitAdventure() {
      this.loading = true;
      this.$apollo
        .mutate({
          mutation: UPDATE_ADVENTURE,
          variables: {
            input: { ...this.$store.getters['adventures/apolloMutationInput'], status: 'active' },
            updateAdventureId: this.$store.getters['adventures/adventureDraft'].id,
          },
        })
        .then((payload) => {
          this.loading = false;
          const receivedStatus = payload.data.updateAdventure.adventure.status;
          if (receivedStatus === 'active') {
            this.showSuccessPopup = true;
          } else if (receivedStatus === 'rejected') {
            this.adventureStatus = 'rejected';
          } else {
            this.adventureStatus = 'error';
          }
        })
        .catch((error) => {
          this.loading = false;
          this.adventureStatus = 'error';
          console.error(error); // eslint-disable-line no-console
        });
    },
  },
};
</script>
